
import { Component, Vue } from 'vue-property-decorator';
import { Route, NavigationGuardNext } from 'vue-router';
import { Action, Getter } from 'vuex-class';
import { Debounce } from '@/utils/decorators';
import Toast from '@/utils/toast';
import InquiryRequestDetails from '@/components/inquiries/InquiryRequestDetails.vue';
import InquiryRequestFilters from '@/components/inquiries/InquiryRequestFilters.vue';
import InquiryRequestList from '@/components/inquiries/InquiryRequestList.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';

const namespace: string = 'inquiryForm';

@Component<Inquiries>({
	components: {
		InquiryRequestDetails,
		InquiryRequestFilters,
		InquiryRequestList,
		SuitesHeader
	}
})
export default class Inquiries extends Vue {
	@Action('fetchInquiryFormRequests', { namespace }) private fetchInquiryFormRequests!: () => Promise<void>;
	@Action('updateInquiryFormRequests', { namespace }) private updateInquiryFormRequests!: (request: InquiryFormRequest) => Promise<void>;
	@Action('setSelectedInquiryFormRequest', { namespace }) private setSelectedInquiryFormRequest!: (request: InquiryFormRequest | null) => void;
	@Action('setInquiryFormFilters', { namespace }) private setInquiryFormFilters!: (filters: InquiryFormFilters) => void;
	@Action('setCurrentPage', { namespace }) private setCurrentPage!: (page: number) => void;
	@Getter('getInquiryFormRequests', { namespace }) private requests!: InquiryFormRequest[];
	@Getter('getSelectedInquiryFormRequest', { namespace }) private storedSelectedRequest!: InquiryFormRequest | null;
	@Getter('getCurrentPage', { namespace }) private currentPage!: number;
	@Getter('getTotalPages', { namespace }) private totalPages!: number;
	private selectedRequest: InquiryFormRequest | null = null;
	private loading: boolean = false;
	private loadingPage: boolean = false;

	private async created(): Promise<void> {
		if (this.storedSelectedRequest) {
			this.selectedRequest = { ...this.storedSelectedRequest };
		}
		await this.loadRequests(true);
	}

	private beforeRouteLeave(to: Route, _from: Route, next: NavigationGuardNext<Inquiries>) {
		if (to.path !== '/order-management/create') {
			this.setSelectedInquiryFormRequest(null);
			this.setInquiryFormFilters({ orderBy: 'created_at' });
			this.setCurrentPage(1);
		}
		next();
	}

	/**
	 * Debounced wrapper for fetching requests when filters change
	 *
	 * @param {boolean} [showLoading=false]
	 * @return {Promise<void>}
	 */
	@Debounce(200)
	private async fetchRequests(showLoading: boolean = false): Promise<void> {
		this.selectedRequest = null;
		await this.loadRequests(showLoading);
	}

	/**
	 * Fetch inquiry requests using the current store filters, resetting to page 1.
	 *
	 * @param {boolean} [showLoading=false]
	 * @return {Promise<void>}
	 */
	private async loadRequests(showLoading: boolean = false): Promise<void> {
		this.loading = showLoading;
		try {
			this.setCurrentPage(1);
			await this.fetchInquiryFormRequests();
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.inquiries.error_fetching', { errorMessage }));
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Navigate to the next or previous page of inquiry requests.
	 *
	 * @param {-1 | 1} direction
	 * @return {Promise<void>}
	 */
	private async changePage(direction: -1 | 1): Promise<void> {
		const previousPage = this.currentPage;
		this.setCurrentPage(this.currentPage + direction);
		this.loadingPage = true;
		try {
			await this.fetchInquiryFormRequests();
		}
		catch (errorMessage) {
			this.setCurrentPage(previousPage);
			Toast.error(this.$t('orders.inquiries.error_fetching', { errorMessage }));
		}
		finally {
			this.loadingPage = false;
		}
	}

	/**
	 * Select an inquiry request and mark it as read.
	 *
	 * @param {InquiryFormRequest} request
	 * @return {Promise<void>}
	 */
	private async updateSelectedRequest(request: InquiryFormRequest): Promise<void> {
		this.setSelectedInquiryFormRequest(request);
		this.selectedRequest = request;
		if (!request.read) {
			await this.updateInquiryFormRequests({
				...request,
				read: true
			});
		}
	}

	/**
	 * Store the selected request and navigate to the order creation page.
	 *
	 * @return {void}
	 */
	private handleNewQuote(): void {
		this.$router.push(`/order-management/create?inquiryRequestId=${this.selectedRequest!.id}`).catch(() => {});
	}

	/**
	 * Navigate to the modify order flow for the selected quote.
	 *
	 * @return {void}
	 */
	private openQuote(orderId: number): void {
		this.$router.push(`/order-management/${orderId}?inquiryRequestId=${this.selectedRequest!.id}`).catch(() => {});
		this.setSelectedInquiryFormRequest(this.selectedRequest);
	}
}
